import React from "react";

/**
 * Flexible dashboard widgets used by StoreDashboardPage.
 *
 * These mirror the visual design of MetricCard / QuickAction but are built to
 * FILL their parent container (h-full + min-h-0) so the dashboard can be laid
 * out as a height-distributed flex column that never scrolls. Font sizes use
 * clamp()/vh so values scale down on short viewports instead of overflowing.
 *
 * Everything visual — fills, borders, corners, shadows and the label/number
 * typography — comes from the `--dash-*` CSS custom properties set on the
 * dashboard root (see config/dashboardTheme.ts). That is what lets a preset
 * turn panels into bare outlines or buttons into rules without these components
 * knowing anything about it. Layout is not themeable and stays here.
 */

/** clamp() sizing, scaled by the preset's font-scale. */
const scaled = (min: string, preferred: string, max: string) =>
  `calc(clamp(${min}, ${preferred}, ${max}) * var(--dash-font-scale, 1))`;

const panelStyle: React.CSSProperties = {
  background: "var(--dash-panel-fill)",
  border: "var(--dash-border-width) solid var(--dash-panel-border)",
  borderRadius: "var(--dash-radius)",
  boxShadow: "var(--dash-shadow)",
};

/** Panel titles and metric labels share the preset's label treatment. */
const labelStyle: React.CSSProperties = {
  textTransform: "var(--dash-label-case)" as React.CSSProperties["textTransform"],
  letterSpacing: "var(--dash-label-tracking)",
};

interface PanelProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
}

export const DashPanel: React.FC<PanelProps> = ({
  title,
  children,
  className = "",
  action,
}) => (
  <div
    style={panelStyle}
    className={`p-3 sm:p-4 flex flex-col min-h-0 overflow-hidden ${className}`}
  >
    {(title || action) && (
      <div className="flex items-center justify-between gap-2 mb-2 shrink-0">
        {title && (
          <h3
            style={{
              ...labelStyle,
              color: "var(--dash-heading)",
              fontWeight: "var(--dash-heading-weight)" as any,
              fontSize: scaled("0.875rem", "2.2vh", "1rem"),
            }}
          >
            {title}
          </h3>
        )}
        {action}
      </div>
    )}
    <div className="flex-1 min-h-0">{children}</div>
  </div>
);

interface DashMetricCardProps {
  title: string;
  value: string;
  icon?: React.ReactNode;
  trend?: "up" | "down";
  clickable?: boolean;
  onClick?: () => void;
}

export const DashMetricCard: React.FC<DashMetricCardProps> = ({
  title,
  value,
  icon,
  trend = "up",
  clickable = false,
  onClick,
}) => {
  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
      window.dispatchEvent(new CustomEvent("shakeTickets"));
    }
  };

  const up = trend === "up";

  return (
    <div
      onClick={handleClick}
      style={panelStyle}
      className={`p-3 sm:p-4 h-full min-h-0 flex flex-col justify-center transition-all duration-300 ${
        clickable
          ? "cursor-pointer hover:brightness-105 hover:scale-[1.02] active:scale-95"
          : ""
      }`}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="min-w-0">
          <p
            className="font-medium truncate"
            style={{
              ...labelStyle,
              color: "var(--dash-muted)",
              fontSize: scaled("0.75rem", "1.9vh", "0.875rem"),
            }}
          >
            {title}
          </p>
          <p
            style={{
              fontSize: scaled("1.1rem", "3.4vh", "1.875rem"),
              color: "var(--dash-value)",
              fontFamily: "var(--dash-numeric-font)",
            }}
            className="font-bold leading-tight truncate"
          >
            {value}
          </p>
        </div>
        {!!icon && (
          <div
            className="p-2 sm:p-3 shrink-0"
            style={{
              borderRadius: "calc(var(--dash-radius) * 0.75)",
              background: up
                ? "var(--dash-icon-bg)"
                : "var(--dash-icon-bg-down)",
              color: up ? "var(--dash-icon-text)" : "var(--dash-icon-text-down)",
              border: `var(--dash-border-width) solid ${
                up ? "var(--dash-icon-border)" : "var(--dash-icon-border-down)"
              }`,
            }}
          >
            {icon}
          </div>
        )}
      </div>
    </div>
  );
};

interface DashQuickActionProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: "emerald" | "red" | "blue";
}

export const DashQuickAction: React.FC<DashQuickActionProps> = ({
  title,
  description,
  icon,
  onClick,
  variant = "emerald",
}) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      background: `var(--dash-action-${variant}-bg)`,
      color: `var(--dash-action-${variant}-fg)`,
      border: `var(--dash-border-width) solid var(--dash-action-${variant}-bd)`,
      borderRadius: "calc(var(--dash-radius) * 0.75)",
      boxShadow: "var(--dash-shadow)",
    }}
    className="group h-full w-full min-h-0 flex items-center gap-2 p-2 sm:p-3 overflow-hidden hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 active:translate-y-0.5 transition-all duration-200 text-left cursor-pointer"
  >
    <div
      className="flex-shrink-0 rounded-lg p-1.5 sm:p-2"
      style={{ background: "var(--dash-action-chip)" }}
    >
      {icon}
    </div>
    <div className="min-w-0">
      <p
        className="font-semibold truncate"
        style={{ ...labelStyle, fontSize: scaled("0.75rem", "1.9vh", "0.875rem") }}
      >
        {title}
      </p>
      <p
        className="truncate hidden sm:block opacity-75"
        style={{ fontSize: scaled("0.625rem", "1.5vh", "0.75rem") }}
      >
        {description}
      </p>
    </div>
  </button>
);

interface DashTileProps {
  onClick: () => void;
  /** fill / rule / label for this tile, from the preset (see tileSurface) */
  surface: React.CSSProperties;
  children: React.ReactNode;
}

export const DashTile: React.FC<DashTileProps> = ({
  onClick,
  surface,
  children,
}) => (
  <div
    onClick={onClick}
    style={{
      ...surface,
      borderRadius: "calc(var(--dash-radius) * 0.75)",
      boxShadow: "var(--dash-shadow)",
    }}
    className="flex items-center justify-center h-full min-h-0 transition-all duration-200 cursor-pointer hover:scale-105 hover:brightness-105"
  >
    <span
      style={{
        fontSize: scaled("0.85rem", "2.6vh", "1.125rem"),
        fontFamily: "var(--dash-numeric-font)",
        letterSpacing: "var(--dash-label-tracking)",
      }}
      className="font-bold capitalize"
    >
      {children}
    </span>
  </div>
);
